import { create } from 'zustand'

export type ShellFocusMode = 'full' | 'center'
export type ShellThemeMode = 'system' | 'light' | 'dark'

const DEFAULT_RIGHT_PANEL_WIDTH = 384
const MIN_RIGHT_PANEL_WIDTH = 320
const MAX_RIGHT_PANEL_WIDTH = 900
const DEFAULT_LEFT_PANEL_WIDTH = 256
const MIN_LEFT_PANEL_WIDTH = 220
const MAX_LEFT_PANEL_WIDTH = 420
const THEME_STORAGE_KEY = 'agent-os:theme'

function getBrowserStorage() {
  if (
    typeof window === 'undefined' ||
    typeof window.localStorage?.getItem !== 'function' ||
    typeof window.localStorage?.setItem !== 'function'
  ) {
    return null
  }

  return window.localStorage
}

function clampRightPanelWidth(width: number) {
  return Math.min(MAX_RIGHT_PANEL_WIDTH, Math.max(MIN_RIGHT_PANEL_WIDTH, width))
}

function clampLeftPanelWidth(width: number) {
  return Math.min(MAX_LEFT_PANEL_WIDTH, Math.max(MIN_LEFT_PANEL_WIDTH, width))
}

function getInitialRightPanelWidth() {
  if (typeof window === 'undefined') {
    return DEFAULT_RIGHT_PANEL_WIDTH
  }

  const storedValue = getBrowserStorage()?.getItem('agent-os:right-panel-width')
  const parsedValue = storedValue ? Number(storedValue) : NaN
  return Number.isFinite(parsedValue)
    ? clampRightPanelWidth(parsedValue)
    : DEFAULT_RIGHT_PANEL_WIDTH
}

function getInitialLeftPanelWidth() {
  if (typeof window === 'undefined') {
    return DEFAULT_LEFT_PANEL_WIDTH
  }

  const storedValue = getBrowserStorage()?.getItem('agent-os:left-panel-width')
  const parsedValue = storedValue ? Number(storedValue) : NaN
  return Number.isFinite(parsedValue)
    ? clampLeftPanelWidth(parsedValue)
    : DEFAULT_LEFT_PANEL_WIDTH
}

function getInitialThemeMode(): ShellThemeMode {
  if (typeof window === 'undefined') {
    return 'system'
  }

  const storedValue = getBrowserStorage()?.getItem(THEME_STORAGE_KEY)
  if (storedValue === 'light' || storedValue === 'dark' || storedValue === 'system') {
    return storedValue
  }

  return 'system'
}

interface ShellStore {
  focusMode: ShellFocusMode
  themeMode: ShellThemeMode
  leftPanelWidth: number
  rightPanelVisible: boolean
  rightPanelWidth: number
  buildSidebarCollapsed: boolean
  rightPanelManuallyToggled: boolean
  autoShowSuppressed: boolean
  setFocusMode: (focusMode: ShellFocusMode) => void
  setThemeMode: (themeMode: ShellThemeMode) => void
  toggleFocusMode: () => void
  setLeftPanelWidth: (width: number) => void
  setRightPanelVisible: (visible: boolean) => void
  toggleRightPanel: () => void
  setRightPanelWidth: (width: number) => void
  setBuildSidebarCollapsed: (collapsed: boolean) => void
  setRightPanelManuallyToggled: (toggled: boolean) => void
  suppressPanel: () => void
  clearAutoShowSuppressed: () => void
}

export const useShellStore = create<ShellStore>((set) => ({
  focusMode: 'full',
  themeMode: getInitialThemeMode(),
  leftPanelWidth: getInitialLeftPanelWidth(),
  rightPanelVisible: true,
  rightPanelWidth: getInitialRightPanelWidth(),
  buildSidebarCollapsed: false,
  rightPanelManuallyToggled: false,
  autoShowSuppressed: false,
  setFocusMode: (focusMode) => set({ focusMode }),
  setThemeMode: (themeMode) => {
    getBrowserStorage()?.setItem(THEME_STORAGE_KEY, themeMode)
    set({ themeMode })
  },
  toggleFocusMode: () =>
    set((state) => ({
      focusMode: state.focusMode === 'full' ? 'center' : 'full',
    })),
  setLeftPanelWidth: (leftPanelWidth) => {
    const clampedWidth = clampLeftPanelWidth(leftPanelWidth)
    getBrowserStorage()?.setItem(
      'agent-os:left-panel-width',
      String(clampedWidth),
    )
    set({ leftPanelWidth: clampedWidth })
  },
  setRightPanelVisible: (rightPanelVisible) => set({ rightPanelVisible }),
  toggleRightPanel: () =>
    set((state) => ({
      rightPanelVisible: !state.rightPanelVisible,
      rightPanelManuallyToggled: true,
      autoShowSuppressed: false,
    })),
  setRightPanelWidth: (rightPanelWidth) => {
    const clampedWidth = clampRightPanelWidth(rightPanelWidth)
    getBrowserStorage()?.setItem(
      'agent-os:right-panel-width',
      String(clampedWidth),
    )
    set({ rightPanelWidth: clampedWidth })
  },
  setBuildSidebarCollapsed: (buildSidebarCollapsed) => set({ buildSidebarCollapsed }),
  setRightPanelManuallyToggled: (rightPanelManuallyToggled) => set({ rightPanelManuallyToggled }),
  suppressPanel: () => set({ autoShowSuppressed: true, rightPanelVisible: false, rightPanelManuallyToggled: false }),
  clearAutoShowSuppressed: () => set({ autoShowSuppressed: false }),
}))
